import React, { useState, useEffect } from 'react';
import { getOrders } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FileText, RefreshCw, Search, Loader2, ChevronRight, Receipt, CreditCard, Banknote } from 'lucide-react';
import { getPaymentMethodLabel } from '../../../lib/paymentMethods';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  completed: { label: 'Abgeschlossen', color: 'bg-emerald-100 text-emerald-700' },
  open:      { label: 'Offen',         color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Storniert',     color: 'bg-red-100 text-red-700' },
  parked:    { label: 'Geparkt',       color: 'bg-amber-100 text-amber-700' },
};

const PAYMENT_ICONS = { cash: Banknote, card: CreditCard, card_sumup: CreditCard, card_manual: CreditCard };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-700' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-AT');
}

async function fetchOrdersForFilters(filterDate, filterStatus) {
  const status = filterStatus === 'all' ? null : filterStatus;
  return getOrders(status, filterDate || null);
}

export default function AdminReportsOrders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadForFilters() {
      setIsLoading(true);
      try {
        const data = await fetchOrdersForFilters(filterDate, filterStatus);
        if (!cancelled) {
          setOrders(data);
        }
      } catch {
        if (!cancelled) {
          toast.error('Fehler beim Laden');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadForFilters();
    return () => {
      cancelled = true;
    };
  }, [filterDate, filterStatus]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      setOrders(await fetchOrdersForFilters(filterDate, filterStatus));
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const num = String(o.order_number || o.id || '');
    const table = String(o.table_name || o.table_id || '');
    return num.includes(search) || table.toLowerCase().includes(search.toLowerCase());
  });

  const totalRevenue = filtered
    .filter(o => o.status === 'completed')
    .reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Bestellungen</h1>
          <p className="text-slate-500">{filtered.length} Bestellungen · €{totalRevenue.toFixed(2)} Umsatz</p>
        </div>
        <Button variant="outline" onClick={loadOrders}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="w-44"
        />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="completed">Abgeschlossen</SelectItem>
            <SelectItem value="open">Offen</SelectItem>
            <SelectItem value="cancelled">Storniert</SelectItem>
            <SelectItem value="parked">Geparkt</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Tisch oder Nr. suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 w-52"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-[#1a2f38]" />
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Bestellung</TableHead>
                  <TableHead>Tisch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zahlung</TableHead>
                  <TableHead className="text-right">Gesamt</TableHead>
                  <TableHead className="text-right">Uhrzeit</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((order) => {
                  const PayIcon = PAYMENT_ICONS[order.payment_method] || Receipt;
                  return (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelected(order)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-slate-500" />
                          </div>
                          <span className="font-mono text-sm font-medium">
                            #{order.order_number || order.id?.slice(-6)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{order.table_name || order.table_id || '—'}</TableCell>
                      <TableCell><StatusBadge status={order.status} /></TableCell>
                      <TableCell>
                        {order.payment_method
                          ? <div className="flex items-center gap-1.5 text-slate-600">
                              <PayIcon className="w-3.5 h-3.5" />
                              <span className="text-sm">{getPaymentMethodLabel(order.payment_method)}</span>
                            </div>
                          : <span className="text-slate-300">—</span>
                        }
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        €{(order.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-slate-500 text-sm tabular-nums">
                        {formatTime(order.created_at)}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Keine Bestellungen gefunden</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Order detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Bestellung #{selected?.order_number || selected?.id?.slice(-6)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs mb-1">Tisch</p>
                  <p className="font-medium">{selected.table_name || selected.table_id || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs mb-1">Status</p>
                  <StatusBadge status={selected.status} />
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs mb-1">Datum</p>
                  <p className="font-medium">{formatDate(selected.created_at)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs mb-1">Uhrzeit</p>
                  <p className="font-medium">{formatTime(selected.created_at)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Positionen</p>
                <div className="divide-y border rounded-lg">
                  {(selected.items || []).map((item, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex-1">{item.quantity}× {item.name}</span>
                      <span className="font-medium tabular-nums">€{((item.price || 0) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 flex items-center justify-between font-semibold">
                <span>Gesamt</span>
                <span className="text-lg">€{(selected.total || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
